import { BrowserModule } from '@angular/platform-browser'
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core'
import { AppComponent } from './app.component'
import { AppMaterialModule } from './app.material.module'
import { environment } from '../environments/environment'
import { MainComponent } from './pages/main/main.component'
import { AppRoutingModule } from './app.routing.module'
import { CallbackComponent } from './pages/callback/callback.component'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { NewKeySystemComponent } from './pages/main/new-key-system/new-key-system.component'
import { FindKeySystemComponent } from './pages/main/find-key-system/find-key-system.component'
import { KeySystemComponent } from './pages/key-system/key-system.component'
import { DashboardComponent } from './pages/key-system/dashboard/dashboard.component'
import { HierarchyComponent } from './pages/key-system/hierarchy/hierarchy.component'
import { HttpClientInterceptor } from './interceptors/http.client.interceptor'
import { KeyingAppEnvironmentConfig } from './configuration/keying.app.environment.config'
import { KeyingAuthConfig } from './configuration/keying.auth.config'
import { DataTableComponent } from './components/data-table/data-table.component'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { HistoryPageComponent } from './pages/key-system/history.page/history.page.component'
import { MomentModule } from 'ngx-moment'
import { BudgetComponent } from './pages/key-system/budget/budget.component'
import { KeyingUserModule } from '@overtur-monorepo/keying-user'
import { KeyingHierarchyModule } from '@overtur-monorepo/keying-hierarchy'
import { KeyingCombinationModule } from '@overtur-monorepo/keying-combination'
import { ArrowDivDirective } from './directives/arrow-div.directive'
import { KeyBoardService } from './services/keyboard.service'
import { RegistryNumberComponent } from './pages/key-system/registry-number/registry-number.component'
import { LoadingComponent } from './components/system/loading/loading.component'
import { SystemTopBarComponent } from './components/system/system-top-bar/system-top-bar.component'
import { DataLoadingComponent } from './components/system/data-loading/data-loading.component'
import { NothingHereComponent } from './components/system/nothing-here/nothing-here.component'
import { ShearlineInputArrayComponent } from './components/cylinder/shearline-input-array/shearline-input-array.component'
import { BreadCrumbsComponent } from './components/system/bread-crumbs/bread-crumbs.component'
import { OrderHistoryComponent } from './components/cylinder/order-history/order-history.component'
import { OperatedByTableComponent } from './components/cylinder/operated-by-table/operated-by-table.component'
import { DealerMasterKeyComponent } from './pages/key-system/dealer-masterkey/dealer-masterkey.component'
import { OrderDetailComponent } from './pages/key-system/order-detail/order-detail.component'
import { OrderInfoComponent } from './pages/key-system/order-detail/order-info/order-info.component'
import { AuditTrailComponent } from './pages/key-system/order-detail/audit-trail/audit-trail.component'
import { ToolsComponent } from './pages/key-system/tools/tools.component'
import { AppContainerModule } from './app-container/app-container.module'
import { SearchComponent } from './components/search/search.component'
/* #region  Matrix */
import { CylinderPinningMatrixComponent } from './components/matrix/cylinder-pinning-matrix/cylinder-pinning-matrix.component'
import { KbaInputArrayComponent } from './components/matrix/kba-input-array/kba-input-array.component'
import { KeyBittingMatrixComponent } from './components/matrix/key-bitting-matrix/key-bitting-matrix.component'
import { MatrixComponent } from './components/matrix/matrix.component'
import { MatrixTopBarComponent } from './components/matrix/matrix-top-bar/matrix-top-bar.component'
import { SpecialFunctionKeysComponent } from './components/matrix/special-function-keys/special-function-keys.component'
/* #endregion Matrix */

/* #region  Library */
import { CylinderLibraryComponent } from './components/library/cylinder-library/cylinder-library.component'
import { KeyBittingArrayComponent } from './components/library/key-bitting-array/key-bitting-array.component'
import { KeyBittingLibraryComponent } from './components/library/key-bitting-library/key-bitting-library.component'
import { LibraryListItemComponent } from './components/library/library-list-item/library-list-item.component'
/* #endregion Library */

/* #region  Dialogs */
import { AddSelectiveMasterDialogComponent } from './dialogs/add-selective-master-dialog/add-selective-master-dialog.component'
import { AssignDialogComponent } from './dialogs/assign-dialog/assign-dialog.component'
import { ConfirmDialogComponent } from './dialogs/confirm-dialog/confirm-dialog.component'
import { AddCompetitorPinningDialog } from './dialogs/add-competitor-pinning-dialog/add-competitor-pinning-dialog.component'
import { CrossKeyDialogComponent } from './dialogs/cross-key-dialog/cross-key-dialog.component'
import { CrossKeyRangeDialogComponent } from './dialogs/cross-key-range-dialog/cross-key-range-dialog.component'
import { GenerateTmkDialogComponent } from './dialogs/generate-tmk-dialog/generate-tmk-dialog.component'
import { NewStructureDialogComponent } from './dialogs/new-structure-dialog/new-structure-dialog.component'
import { ReserveDialogComponent } from './dialogs/reserve-dialog/reserve-dialog.component'
import { ThruNotationDialogComponent } from './dialogs/thru-notation-dialog/thru-notation-dialog.component'
import { MoveKeysDialogComponent } from './dialogs/move-keys-dialog/move-keys-dialog.component'
import { RenameKeysDialogComponent } from './dialogs/rename-keys-dialog/rename-keys-dialog.component'
import { ConstructionKeyLibraryComponent } from './components/library/construction-key-library/construction-key-library.component'
import { ConstructionKeyMatrixComponent } from './components/matrix/construction-key-matrix/construction-key-matrix.component'
import { EditNoteComponent } from './dialogs/edit-note-dialog/edit-note-dialog.component'
import { InboundOutboundDialogComponent } from './dialogs/inbound-outbound-info-dialog/inbound-outbound-info-dialog.component'
import { ChangeKeyTypeDialogComponent } from './dialogs/change-key-type-dialog/change-key-type-dialog.component'
import { ReportsDialogComponent } from './dialogs/reports-dialog/reports-dialog.component'
import { MecsErrorsDialogComponent } from './dialogs/mecs-errors-dialog/mecs-errors-dialog.component'
import { MoveBittingsDialogComponent } from './dialogs/move-bittings-dialog/move-bittings-dialog.component'
import { NotificationDetailDialogComponent } from './dialogs/notification-detail-dialog/notification-detail-dialog.component'
/* #endregion Dialogs */

import { KanbanBoardComponent } from './components/kanban-board/kanban-board.component'
import { KanbanCardComponent } from './components/kanban-board/kanban-card/kanban-card.component'
import { ScrollingModule } from '@angular/cdk/scrolling'
import { StructureComponent } from './pages/structure/structure.component'
import { IndependentKeySystemsComponent } from './pages/structure/independent-key-systems/independent-key-systems.component'
import { NewKeySystemDialogComponent } from './dialogs/new-key-system-dialog/new-key-system-dialog.component'
import { StructureCardComponent } from './components/system/structure-card/structure-card.component'
import { KeySystemCardComponent } from './components/system/key-system-card/key-system-card.component'
import { HistoryStructurePageComponent } from './pages/structure/history.structure.page/history.structure.page.component'
import { PhxAngularMaterialModule } from '@overtur-monorepo/phx-angular-material'
import { MultiSelectionCheckboxDirective } from './directives/multi-checkbox.directive'
import { KeyingSharedModule } from '@overtur-monorepo/keying-shared'
import { ReportsComponent } from './pages/key-system/reports/reports.component'
import { ChangeLogConfirmationDialogComponent } from './components/matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { AddAttachmentsCardComponent } from './components/system/add-attachments-card/add-attachments-card.component'
import { FindOrdersComponent } from './pages/main/keying-orders/find-orders/find-orders.component'
import { MatPaginatorModule } from '@angular/material/paginator'
import { DatePipe } from '@angular/common'
import { NavService } from './services/nav.service'
import { FeatureFlagConfig } from '@overtur-monorepo/keying-shared'
import { FeatureManagementConfig } from '@overtur-monorepo/feature-management'

const ENTRY_COMPONENTS = [
  AddSelectiveMasterDialogComponent,
  AssignDialogComponent,
  AddCompetitorPinningDialog,
  ConfirmDialogComponent,
  CrossKeyDialogComponent,
  CrossKeyRangeDialogComponent,
  GenerateTmkDialogComponent,
  NewStructureDialogComponent,
  ReserveDialogComponent,
  ThruNotationDialogComponent,
  MoveKeysDialogComponent,
  RenameKeysDialogComponent,
  NewKeySystemDialogComponent,
  EditNoteComponent,
  InboundOutboundDialogComponent,
  ChangeKeyTypeDialogComponent,
  FindOrdersComponent,
  ReportsDialogComponent,
  MecsErrorsDialogComponent,
  MoveBittingsDialogComponent,
  NotificationDetailDialogComponent,
]

const keyingAuthConfig: KeyingAuthConfig = new KeyingAuthConfig(
  environment.auth0Config.clientId,
  environment.auth0Config.domain,
  environment.auth0Config.responseType,
  environment.auth0Config.audience,
  environment.auth0Config.redirectUrl,
  environment.auth0Config.scope,
  environment.auth0Config.logoutUrl
)

const keyingAppEnvironmentConfig = KeyingAppEnvironmentConfig.createKeyingEnvironmentConfig({
  masterKeyApiUrl: environment.masterKeyApiUrl,
  isAlohomora: environment.flags.isAlohomora,
})

@NgModule({
  declarations: [
    AppComponent,
    MainComponent,
    CallbackComponent,
    LoadingComponent,
    NewKeySystemComponent,
    FindKeySystemComponent,
    StructureComponent,
    IndependentKeySystemsComponent,
    KeySystemComponent,
    DashboardComponent,
    HierarchyComponent,
    BreadCrumbsComponent,
    DataTableComponent,
    SystemTopBarComponent,
    ReportsComponent,
    HistoryPageComponent,
    AuditTrailComponent,
    OrderDetailComponent,
    OrderInfoComponent,
    ToolsComponent,
    HistoryStructurePageComponent,
    DataLoadingComponent,
    NothingHereComponent,
    ShearlineInputArrayComponent,
    BudgetComponent,
    ArrowDivDirective,
    OrderHistoryComponent,
    OperatedByTableComponent,
    CylinderLibraryComponent,
    KeyBittingArrayComponent,
    KeyBittingLibraryComponent,
    LibraryListItemComponent,
    RegistryNumberComponent,
    CylinderPinningMatrixComponent,
    KbaInputArrayComponent,
    KeyBittingMatrixComponent,
    SpecialFunctionKeysComponent,
    MatrixComponent,
    ChangeLogConfirmationDialogComponent,
    MatrixTopBarComponent,
    ConstructionKeyLibraryComponent,
    ConstructionKeyMatrixComponent,
    KanbanBoardComponent,
    KanbanCardComponent,
    DealerMasterKeyComponent,
    StructureCardComponent,
    KeySystemCardComponent,
    MultiSelectionCheckboxDirective,
    SearchComponent,
    ...ENTRY_COMPONENTS,
    AddAttachmentsCardComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    ScrollingModule,
    BrowserModule,
    AppRoutingModule,
    AppMaterialModule,
    BrowserAnimationsModule,
    KeyingUserModule,
    // MomentModule,
    FormsModule,
    ReactiveFormsModule,
    PhxAngularMaterialModule,
    KeyingSharedModule,
    MatPaginatorModule,
    AppContainerModule,
    KeyingHierarchyModule.forRoot({
      masterKeyApiUrl: environment.masterKeyApiUrl,
      isAlohomora: environment.flags.isAlohomora,
    }),
    KeyingCombinationModule.forRoot({
      masterKeyApiUrl: environment.masterKeyApiUrl,
      isAlohomora: environment.flags.isAlohomora,
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpClientInterceptor,
      multi: true,
    },
    { provide: KeyingAuthConfig, useValue: keyingAuthConfig },
    {
      provide: KeyingAppEnvironmentConfig,
      useValue: keyingAppEnvironmentConfig,
      multi: false,
    },
    {
      provide: FeatureFlagConfig,
      useValue: keyingAppEnvironmentConfig,
    },
    {
      provide: FeatureManagementConfig,
      useValue: keyingAppEnvironmentConfig,
    },
    KeyBoardService,
    NavService,
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
